import { useContext } from 'react';

import { PublishStatus, UserDownloadAccessRestrictions } from '../../types';
import { Asset, assetUri } from '../../utils/assetUri';
import { MetadataContext } from '../../utils/contexts';
import useTranslate from '../../utils/translations';
import { getContentTrackingPropertiesFromMetadata } from '../../utils/useTracking';
import fileDownload from './fileDownload';
import { useCwaAxios } from '../../utils/cwaAxios';

export const useDownloadButtons = (
  downloadPath: string | undefined,
  publishStatus: PublishStatus,
  downloadAccess: UserDownloadAccessRestrictions | undefined,
): Record<string, unknown>[] => {
  const cwaAxios = useCwaAxios();
  const t = useTranslate();
  const metadata = useContext(MetadataContext);

  const infographicId = metadata?.resources
    ?.filter((attachment) => attachment.type === 'infographic-overview')
    .pop()?.id;
  const downloads = [];
  const metadataTrackingProperties = getContentTrackingPropertiesFromMetadata(
    metadata,
  );

  if (!!infographicId && downloadAccess?.infographicOverview) {
    downloads.push({
      title: t('Infographic overview'),
      description: t(
        'Data visualizations of key insights, curated by our experts.',
      ),
      downloads: [
        {
          url: assetUri(infographicId, Asset.Attachment, publishStatus),
          label: t('Download PDF'),
          mixpanelLocation: 'Sidebar',
          mixpanelProperties: {
            ...metadataTrackingProperties,
            Action: 'Download Infographic Overview',
            Panel: 'Download',
            Format: '.pdf',
          },
        },
      ],
    });
  }

  if (!!metadata.has_executive_summary && downloadAccess?.executiveSummary) {
    downloads.push({
      title: t('Executive summary'),
      description: t('Key findings and expert recommendations.'),
      downloads: [
        {
          url: `${downloadPath}/pdf-async?executive_summary=true`,
          label: t('Download PDF'),
          mixpanelLocation: 'Sidebar',
          mixpanelProperties: {
            ...metadataTrackingProperties,
            Action: 'Download Executive Summary',
            Panel: 'Download',
            Format: '.pdf',
          },
          fileDownload: (url: string): Promise<void> =>
            fileDownload(cwaAxios, url),
        },
      ],
    });
  }

  if (!!downloadPath && downloadAccess?.fullReport) {
    const fullReportDownloads = [
      {
        url: `${downloadPath}/pptx-async`,
        label: t('Download PPT'),
        mixpanelLocation: 'Sidebar',
        mixpanelProperties: {
          ...metadataTrackingProperties,
          Action: 'Download PPT',
          Panel: 'Download',
          Format: '.ppt',
        },
        fileDownload: (url: string): Promise<void> =>
          fileDownload(cwaAxios, url),
      },
      {
        url: `${downloadPath}/pdf-async`,
        label: t('Download PDF'),
        mixpanelLocation: 'Sidebar',
        mixpanelProperties: {
          ...metadataTrackingProperties,
          Action: 'Download PDF',
          Panel: 'Download',
          Format: '.pdf',
        },
        fileDownload: (url: string): Promise<void> =>
          fileDownload(cwaAxios, url),
      },
    ];
    downloads.push({
      title: t('Full report'),
      description: t('The in-depth analysis behind the insights.'),
      downloads: fullReportDownloads,
    });
  }

  return downloads;
};
